import {
  GAME_ID,
  ITEMS_COUNT_IN_PRACTICE,
  NUM_FAILED_ITEMS_BEFORE_PRACTICE,
  ITEMS_COUNT_IN_ROUND,
  REDUX_ACTIONS,
} from '../../data/constants';
import { getCurriculum } from '../../content/curriculum';

const initialState = {
  standardItemsCompleted: 0, // amount of standard items completed in the current round
  practiceItemsCompleted: 0, // amount of practice items completed in the current practice set. Resets when a new practice set is triggered.

  amountCorrect: 0, // amount of standard items correctly answered.
  amountIncorrect: 0, // amount of standard items incorrectly answered. Resets when a new practice set is triggered.
  practiceRoundsComplete: 0, // amount of practice items completed in the current practice set.

  inPractice: false,
  inOnboarding: false,
  onlyOnboarding: false, // wether or not we should be doing _only_ onboarding. Is usually false.

  currentLevel: 0,
  practiceIndex: 0, // current position in the content doc's list of practice items
  currentItemIndicesForRound: [], // pre-decided list of indices pulled from the content doc's list of standard items.
  standardIndex: 0, // which index from currentItemIndicesForRound to use for the current sentence.

  currentSentence: null,

  masteredItems: null, // all the items that the player has gotten correct and 'saved'
  pendingMasteredItems: [], // the items that the player has gotten correct in the current round, which have not yet been saved.

  roundFinished: false,
  highestLevelEarned: 0,
  hasDoneOnboarding: false,
};

const curriculumReducer = (state = initialState, action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }
  switch (action.type) {
    case REDUX_ACTIONS.BEGIN_ROUND: {
      let { standardIndex } = state;
      const { standardItems } = getCurriculum();
      const masteredItems = state.masteredItems ?? Array.from({ length: standardItems.length }, () => []);
      if (action.payload.level !== state.currentLevel) {
        standardIndex = 0; // reset to the start if we're on a new level
      } else {
        standardIndex += 1; // or move on to the item after the one we were just on if we haven't changed levels.
      }
      const currentItemIndicesForRound = [];
      const sentencesForLevel = standardItems[action.payload.level];
      const allIndices = Array(sentencesForLevel.length)
        .fill(0)
        .map((_, i) => (i + standardIndex) % sentencesForLevel.length);
      const allowedIndices = [];
      const fillerIndices = [];
      // partition the list of indices into two sets. One for items the player has already mastered, another for items the player hasn't mastered.
      for (let i = 0; i < allIndices.length; i++) {
        if (masteredItems[action.payload.level].includes(allIndices[i])) {
          fillerIndices.push(allIndices[i]);
        } else {
          allowedIndices.push(allIndices[i]);
        }
      }

      // fill the currentItemIndicesForRound array, giving priority to items the player hasn't mastered yet.
      for (let i = 0; i < ITEMS_COUNT_IN_ROUND; i++) {
        if (allowedIndices.length > 0) {
          currentItemIndicesForRound.push(allowedIndices[0]);
          allowedIndices.splice(0, 1);
        } else {
          currentItemIndicesForRound.push(fillerIndices[0]);
          fillerIndices.splice(0, 1);
        }
      }

      return {
        ...state,
        currentLevel: action.payload.level,
        standardItemsCompleted: 0,
        practiceItemsCompleted: 0,
        amountCorrect: 0,
        amountIncorrect: 0,
        practiceRoundsComplete: 0,
        inPractice: false,
        inOnboarding: action.payload.withOnboarding,
        onlyOnboarding: action.payload.onlyOnboarding,
        roundFinished: false,
        currentItemIndicesForRound,
        standardIndex,
        currentSentence: null,
        pendingMasteredItems: [],
        masteredItems,
      };
    }

    case REDUX_ACTIONS.COMPLETE_ITEM: {
      let {
        practiceItemsCompleted,
        standardItemsCompleted,
        amountCorrect,
        amountIncorrect,
        inPractice,
        roundFinished,
      } = state;
      const pendingMasteredItems = [...state.pendingMasteredItems];

      if (!state.inOnboarding) {
        if (inPractice) {
          // note that you've finished a practice item and then move on if you've completed enough to exit practice.
          practiceItemsCompleted++;
          if (practiceItemsCompleted >= ITEMS_COUNT_IN_PRACTICE) {
            practiceItemsCompleted = 0;
            inPractice = false;
            if (
              standardItemsCompleted >= ITEMS_COUNT_IN_ROUND
            ) {
              roundFinished = true;
            }
          }
        } else {
          // note that you've completed a standard item
          standardItemsCompleted++;
          if (action.payload.firstTry) {
            // handle all the rewards and bookkeeping for a correct item.
            amountCorrect++;
            if (
              standardItemsCompleted >= ITEMS_COUNT_IN_ROUND
            ) {
              roundFinished = true;
            }
            amountIncorrect = 0; // user must get two wrong in a row to go to practice mode.
            const masteredIndex =
              state.currentItemIndicesForRound[state.standardItemsCompleted];
            // if this item hasn't yet been mastered,
            if (
              !state.masteredItems[state.currentLevel].includes(masteredIndex)
            ) {
              pendingMasteredItems.push(masteredIndex); // log it as having been mastered
            }
          } else {
            // handle all of the bookkeeping for an incorrect item.
            amountIncorrect++;
            if (amountIncorrect >= NUM_FAILED_ITEMS_BEFORE_PRACTICE) {
              inPractice = true;
              amountIncorrect = 0;
            } else if (
              standardItemsCompleted >= ITEMS_COUNT_IN_ROUND
            ) {
              roundFinished = true;
            }
          }
        }
      } else if (state.onlyOnboarding) {
        roundFinished = true; // if we were in onboarding, and were only supposed to do onboarding, then we can end the round early.
      }

      return {
        ...state,
        practiceItemsCompleted,
        standardItemsCompleted,
        amountCorrect,
        amountIncorrect,
        inOnboarding: false,
        inPractice,
        roundFinished,
        pendingMasteredItems,
      };
    }

    case REDUX_ACTIONS.CHOOSE_NEXT_SENTENCE: {
      let currentSentence = null;
      let { standardIndex, practiceIndex } = state;
      const { standardItems, practiceItems, onboardingItem } = getCurriculum();

      // get the next sentence in the list.
      // needs to check your level and if you're in a practice vs. standard item to determine the next sentence.
      if (state.inOnboarding) {
        currentSentence = onboardingItem;
      } else if (state.inPractice) {
        practiceIndex =
          (practiceIndex + 1) %
          practiceItems[state.currentLevel].length;
        currentSentence =
          practiceItems[state.currentLevel][practiceIndex];
      } else {
        const currentItemIndex =
          state.currentItemIndicesForRound[
            state.standardItemsCompleted %
          state.currentItemIndicesForRound.length
          ];
        currentSentence = standardItems[state.currentLevel][currentItemIndex];
        if (
          state.standardItemsCompleted < state.currentItemIndicesForRound.length
        ) {
          standardIndex =
            state.currentItemIndicesForRound[state.standardItemsCompleted];
        }
      }

      return {
        ...state,
        currentSentence,
        standardIndex,
        practiceIndex,
      };
    }

    case REDUX_ACTIONS.COMPLETE_ROUND: {
      // bank all the items you've mastered in the current round
      const masteredItems = [...state.masteredItems];
      masteredItems[state.currentLevel] = masteredItems[
        state.currentLevel
      ].concat(state.pendingMasteredItems);

      return {
        ...state,
        masteredItems,
      };
    }

    case REDUX_ACTIONS.UPDATE_PRACTICE_DETAILS: {
      const { highestLevelEarned } = action.payload;

      return {
        ...state,
        highestLevelEarned,
      };
    }

    case REDUX_ACTIONS.SET_HAS_DONE_ONBOARDING: {
      return {
        ...state,
        hasDoneOnboarding: true,
      };
    }

    case REDUX_ACTIONS.CLEAR_PROGRESS:
      return {
        ...state,
        pendingMasteredItems: [],
      };

    case REDUX_ACTIONS.QUICK_ROUND:
      return {
        ...state,
        currentLevel: action.payload.level,
        amountCorrect: action.payload.pass ? ITEMS_COUNT_IN_ROUND : 0,
      };
    default:
      return state;
  }
};

export default curriculumReducer;
